import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { db, auth } from "../FirebaseConfig";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

export default function ChatRoom() {
  const { receiverId } = useParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [receiver, setReceiver] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [isLoadingReceiver, setIsLoadingReceiver] = useState(true);

  const scrollRef = useRef();
  const inputRef = useRef();

  // 1️⃣ Track authenticated user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // 2️⃣ Fetch receiver details (prefer 'profiles' collection)
  useEffect(() => {
    const fetchReceiver = async () => {
      if (!receiverId) {
        setIsLoadingReceiver(false);
        return;
      }
      setIsLoadingReceiver(true);
      try {
        let snap = await getDoc(doc(db, "profiles", receiverId)); // 🔁 Use 'profiles' first
        if (!snap.exists()) {
          snap = await getDoc(doc(db, "users", receiverId)); // fallback to 'users'
        }

        if (snap.exists()) {
          setReceiver({ id: snap.id, ...snap.data() });
        } else {
          setReceiver(null);
        }
      } catch (error) {
        console.error("Error fetching receiver:", error);
        setReceiver(null);
      } finally {
        setIsLoadingReceiver(false);
      }
    };
    fetchReceiver();
  }, [receiverId]);

  // 3️⃣ Real-time message listener
  useEffect(() => {
    if (!currentUser || !receiverId) {
      setIsLoadingMessages(false);
      return;
    }

    setIsLoadingMessages(true);
    const q = query(
      collection(db, "messages"),
      where("participants", "in", [
        [currentUser.uid, receiverId],
        [receiverId, currentUser.uid],
      ]),
      orderBy("timestamp")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setIsLoadingMessages(false);
      },
      (error) => {
        console.error("Error listening to messages:", error);
        setIsLoadingMessages(false);
      }
    );

    return () => unsub();
  }, [currentUser, receiverId]);

  // 4️⃣ Auto-focus & scroll
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isLoadingMessages && messages.length > 0) {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoadingMessages]);

  // 5️⃣ Send message
  const handleSend = async () => {
    if (!newMessage.trim() || !currentUser || !receiverId) return;

    const messageObj = {
      senderId: currentUser.uid,
      receiverId,
      text: newMessage.trim(),
      timestamp: serverTimestamp(),
      participants: [currentUser.uid, receiverId],
    };

    setMessages((prev) => [
      ...prev,
      { id: Date.now(), ...messageObj, timestamp: new Date() },
    ]);

    setNewMessage("");

    try {
      await addDoc(collection(db, "messages"), messageObj);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // 6️⃣ Handle typing (optional logic for future)
  const handleNewMessageChange = (e) => {
    setNewMessage(e.target.value);
  };

  // 7️⃣ Render UI
  if (!currentUser) {
    return (
      <div className="max-w-2xl mx-auto h-[90vh] flex items-center justify-center border rounded shadow mt-4">
        <p className="text-gray-600">Please log in to view chats.</p>
      </div>
    );
  }

  if (isLoadingReceiver) {
    return (
      <div className="max-w-2xl mx-auto h-[90vh] flex items-center justify-center border rounded shadow mt-4">
        <p className="text-gray-600">Loading recipient details...</p>
      </div>
    );
  }

  if (!receiver) {
    return (
      <div className="max-w-2xl mx-auto h-[90vh] flex items-center justify-center border rounded shadow mt-4">
        <p className="text-gray-600">Recipient not found or invalid chat ID.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto h-[90vh] flex flex-col border rounded shadow mt-4">
      {/* Header */}
      <div className="bg-green-600 text-white p-4 flex justify-between items-center">
        <div>
          <div className="font-semibold">{receiver?.name || "Unknown User"}</div>
          <div className="text-sm">
            {receiver?.online ? (
              <span className="text-green-300">🟢 Online</span>
            ) : (
              <span className="text-gray-300">
                Last seen{" "}
                {receiver?.lastSeen?.toDate &&
                  dayjs(receiver.lastSeen.toDate()).fromNow()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto space-y-2 bg-transparent" style={{ overflowX: "hidden" }}>
        {isLoadingMessages ? (
          <div className="flex justify-center items-center h-full">
            <p className="text-gray-500">Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <p className="text-gray-500">Say hello! No messages yet.</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isCurrent = msg.senderId === currentUser?.uid;
            const avatar = isCurrent
              ? auth.currentUser?.photoURL
              : receiver?.profilePic;

            return (
              <div
                key={msg.id}
                className={`flex items-end ${isCurrent ? "justify-end" : "justify-start"} space-x-2`}
              >
                {!isCurrent && (
                  <img
                    src={
                      avatar ||
                      "https://i.pinimg.com/564x/c0/73/f7/c073f7c24358ca24d7ee14cbff657a0e.jpg"
                    }
                    alt="avatar"
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                )}
                <div
                  ref={scrollRef}
                  className={`whitespace-pre-wrap break-words px-3 py-2 text-sm rounded-lg max-w-[70%] min-w-0 ${
                    isCurrent
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 text-black"
                  }`}
                >
                  {msg.text}
                  <div className="text-[10px] text-right mt-1 opacity-70">
                    {msg.timestamp?.toDate
                      ? dayjs(msg.timestamp.toDate()).format("h:mm A")
                      : ""}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t flex items-center">
        <input
          ref={inputRef}
          type="text"
          placeholder="Type your message..."
          className="flex-1 p-2 rounded border"
          value={newMessage}
          onChange={handleNewMessageChange}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
        />
        <button
          onClick={handleSend}
          className="ml-2 bg-green-600 text-white px-4 py-2 rounded"
        >
          Send
        </button>
      </div>
    </div>
  );
}
